import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Eye, EyeOff, AlertCircle, Globe, X, ArrowLeft } from "lucide-react";
import api from "../api/axios";

export const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorType, setErrorType] = useState(null); // "incorrectPassword", "emailNotFound", "notRegistered", "emailNotVerified", veya null
  const { login, loadProjects, setUser } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  // Hata mesajını errorType'a göre çevir - daha kullanıcı dostu ve spesifik
  const getErrorMessage = () => {
    if (errorType === "incorrectPassword") {
      return t.incorrectPassword || (language === "tr"
        ? "Şifre hatalı, lütfen tekrar deneyin"
        : "Password is incorrect, please try again");
    } else if (errorType === "emailNotFound") {
      return t.emailNotFound || (language === "tr"
        ? "Bu e-posta adresiyle bir hesap bulunamadı"
        : "No account found with this email address");
    } else if (errorType === "notRegistered") {
      return t.emailNotRegistered || (language === "tr"
        ? "Bu e-posta adresi sistemde kayıtlı değil"
        : "This email address is not registered in the system");
    } else if (errorType === "emailNotVerified") {
      return t.emailNotVerified || (language === "tr"
        ? "E-posta adresiniz doğrulanmamış. Lütfen e-postanızı kontrol edin ve hesabınızı doğrulayın."
        : "Your email address is not verified. Please check your email and verify your account.");
    }
    return "";
  };

  const languageNames = {
    tr: 'Türkçe',
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
    fr: 'Français'
  };

  // Use global api instance from axios.js (handles HTTPS/proxy correctly)

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (loading) {
      return;
    }
    
    setLoading(true);
    setErrorType(null);

    // Direkt login endpoint'ine istek yap (email check gereksiz, backend handling yapıyor)
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;

      // localStorage'ı güncelle
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // AuthContext'in user state'ini güncelle
      if (setUser) {
        setUser(userData);
      }

      // AuthContext'in loadProjects fonksiyonunu kullan
      if (loadProjects) {
        await loadProjects(null);
      }

      toast.success(t.loginSuccess || "Login successful!");
      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "";
      
      // HTTP status code'una göre hata mesajını belirle
      const statusCode = error.response?.status;
      
      // 403 Forbidden - Email not verified
      if (statusCode === 403) {
        const detail = error.response?.data?.detail;
        const detailLower = (detail || "").toLowerCase();
        if (detailLower.includes("email not verified") || 
            detailLower.includes("email verification") ||
            detailLower.includes("e-posta doğrulanmamış") ||
            detailLower.includes("e-mail nicht verifiziert") ||
            detailLower.includes("correo no verificado") ||
            detailLower.includes("e-mail non vérifié")) {
          setErrorType("emailNotVerified");
          toast.error(t.emailNotVerified || "Email not verified. Please check your email and verify your account.");
          return;
        }
      }
      
      // 401 Unauthorized veya 400 Bad Request genellikle yanlış giriş bilgileri demektir
      if (statusCode === 401 || statusCode === 400) {
        // Backend'den gelen detay mesajını kontrol et
        const detail = error.response?.data?.detail;
        let backendMessage = "";
        
        if (detail) {
          // Eğer detail bir obje ise (validasyon hatası gibi), string'e çevir
          if (typeof detail === 'object') {
            if (Array.isArray(detail)) {
              // Array ise, ilk hatayı al
              backendMessage = detail[0]?.msg || detail[0]?.message || "";
            } else if (detail.msg) {
              backendMessage = detail.msg;
            } else if (detail.message) {
              backendMessage = detail.message;
            }
          } else {
            backendMessage = detail;
          }
        }
        
        // Backend mesajını kontrol et - SPESIFIK mesajları ayırt et
        const backendMessageLower = backendMessage.toLowerCase();

        // 1. Şifre yanlış kontrolü (backend'den yeni spesifik error)
        if (backendMessageLower.includes("incorrect password") ||
            backendMessageLower.includes("passwort ist falsch") ||
            backendMessageLower.includes("la contraseña es incorrecta") ||
            backendMessageLower.includes("le mot de passe est incorrect") ||
            backendMessageLower.includes("şifre hatalı")) {
          setErrorType("incorrectPassword");
        }
        // 2. Email bulunamadı kontrolü (backend'den yeni spesifik error)
        else if (backendMessageLower.includes("email not found") ||
            backendMessageLower.includes("e-mail nicht gefunden") ||
            backendMessageLower.includes("correo no encontrado") ||
            backendMessageLower.includes("e-mail introuvable") ||
            backendMessageLower.includes("e-posta bulunamadı")) {
          setErrorType("emailNotFound");
        }
        // 3. Email kayıtlı değil (eski support, backward compatibility)
        else if (backendMessageLower.includes("not registered") ||
            backendMessageLower.includes("user not found") ||
            backendMessageLower.includes("kayıtlı değil") ||
            backendMessageLower.includes("bulunamadı")) {
          setErrorType("notRegistered");
        }
        // 4. Diğer 401/400 hatalar - şifre yanlış olarak set et (fallback)
        else {
          setErrorType("incorrectPassword");
        }
      } else if (error.response?.data?.detail) {
        // Diğer hata durumları
        const detail = error.response.data.detail;
        if (typeof detail === 'object') {
          if (Array.isArray(detail)) {
            errorMessage = detail[0]?.msg || detail[0]?.message || JSON.stringify(detail[0]);
          } else if (detail.msg) {
            errorMessage = detail.msg;
          } else if (detail.message) {
            errorMessage = detail.message;
          } else {
            errorMessage = JSON.stringify(detail);
          }
        } else {
          errorMessage = detail;
        }

        const errorMessageLower = errorMessage?.toLowerCase() || "";
        if (errorMessageLower.includes("password")) {
          setErrorType("incorrectPassword");
        } else if (errorMessageLower.includes("email") || errorMessageLower.includes("not found")) {
          setErrorType("emailNotFound");
        } else {
          setErrorType("incorrectPassword");
        }
      } else if (error.message) {
        setErrorType("incorrectPassword");
        errorMessage = error.message;
      } else {
        // Varsayılan hata mesajı
        setErrorType("incorrectPassword");
      }
      
      console.error("Login error:", error);
      // errorType zaten set edilmiş, sadece log için errorMessage kullanıyoruz
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(e);
    }
  };


  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Hata mesajını input değiştiğinde silme - sadece submit sırasında silinecek
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    // Hata mesajını input değiştiğinde silme - sadece submit sırasında silinecek
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setErrorType(null);

      // Backend'den Google OAuth URL'ini al (mevcut dil bilgisini gönder)
      const response = await api.get(`/auth/google?hl=${language}`);
      const { authorization_url } = response.data;

      // Full page redirect - popup yerine doğrudan Google OAuth sayfasına yönlendir
      window.location.href = authorization_url;

    } catch (error) {
      console.error("Google login error:", error);
      setErrorType("incorrectPassword");
      toast.error(t.googleLoginError || "Google login failed. Please try again.");
      setLoading(false);
    }
  };

  const LoginContent = (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 flex items-center justify-center p-4 relative">
      {/* Back Button - Sol üst köşe */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 p-2 hover:bg-white/20 dark:hover:bg-gray-800/50 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        aria-label="Go back to home"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Language Selector & Theme Toggle - Sağ üst köşe */}
      <div className="absolute top-3 md:top-4 right-3 md:right-4 flex items-center space-x-2 md:space-x-3">
        <ThemeToggle />
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-[80px] md:w-[140px] text-xs md:text-sm">
            <SelectValue>
              <div className="flex items-center space-x-1 md:space-x-2">
                <Globe className="w-3 md:w-4 h-3 md:h-4" />
                <span className="hidden md:inline">{languageNames[language] || languageNames.tr}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tr" className="text-xs md:text-sm">{languageNames.tr}</SelectItem>
            <SelectItem value="en" className="text-xs md:text-sm">{languageNames.en}</SelectItem>
            <SelectItem value="de" className="text-xs md:text-sm">{languageNames.de}</SelectItem>
            <SelectItem value="es" className="text-xs md:text-sm">{languageNames.es}</SelectItem>
            <SelectItem value="fr" className="text-xs md:text-sm">{languageNames.fr}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <img 
            src="/logo-256.webp" 
            alt="FeedPanels Logo" 
            className="w-40 h-40 object-contain mb-2 mx-auto"
          />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t.welcomeBack || "Hoş Geldiniz"}
          </h1>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle data-testid="login-title" className="dark:text-white">{t.welcomeBack || "Welcome back"}</CardTitle>
            <CardDescription className="dark:text-gray-400">
              {t.signInToContinue || "Sign in to your account to continue"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="dark:text-gray-300">{t.emailLabel || "Email"}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t.emailPlaceholder || "you@company.com"}
                  value={email}
                  onChange={handleEmailChange}
                  onKeyPress={handleKeyPress}
                  data-testid="login-email-input"
                  className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="dark:text-gray-300">{t.passwordLabel || "Password"}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t.passwordPlaceholder || "••••••••"}
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyPress={handleKeyPress}
                    data-testid="login-password-input"
                    className="pr-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  {t.forgotPassword || "Forgot Password?"}
                </Link>
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={loading}
                onClick={handleSubmit}
                data-testid="login-submit-button"
              >
                {loading ? (t.signingIn || "Signing in...") : (t.signIn || "Sign In")}
              </Button>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">
                    {t.orContinueWith || "Or continue with"}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={loading}
                onClick={handleGoogleLogin}
                data-testid="google-login-button"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t.googleSignIn || "Sign in with Google"}
              </Button>
              {errorType && (
                <div className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 dark:bg-red-900/30 dark:border-red-800 px-4 py-3 text-sm text-destructive dark:text-red-400 flex items-center gap-2 justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{getErrorMessage()}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorType(null)}
                    className="text-destructive dark:text-red-400 hover:text-destructive/80 dark:hover:text-red-300 focus:outline-none"
                    aria-label="Close error message"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2 dark:border-t dark:border-gray-700">
            <div className="text-sm text-center text-gray-600 dark:text-gray-400">
              {t.dontHaveAccount || "Don't have an account?"}{" "}
              <Link
                to="/register"
                className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
                data-testid="register-link"
              >
                {t.signUp || "Sign up"}
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Login - FeedPanels</title>
        <meta name="description" content="Sign in to your FeedPanels account. Access your feedback dashboard, roadmap, and changelog." />
        <link rel="canonical" href="https://feedpanels.com/login" />
      </Helmet>
      {LoginContent}
    </>
  );
};
